import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import * as config from '../config/config';
import { ExtractResult, ItemAcountRecherche } from '../models';
import { CdkTableColumns, CdkTableTypeColumn } from '../models/cdk-table.model';
import { FilterCriteria } from '../models/filter-criteria.model';
import { FournisseurService } from '../services/fournisseur.service';
import { HttpServices, QueryParameter } from '../services/http.service';
import { LoaderService } from '../services/loader.service';
import { ReferenceService } from '../services/reference.service';
import { TrackingService } from '../services/tracking.service';
import { UserService } from '../services/user.service';
import * as utils from '../utils/utils';

@Component({
  selector: 'fs-extract-account',
  styleUrls: ['./extract-account.sass'],
  templateUrl: './extract-account.component.html',
  encapsulation: ViewEncapsulation.None
})

export class ExtractAccountComponent implements OnInit, OnDestroy {
  suppliersList;
  activeSupplierId: number;
  urlCSV: string;
  rechercheItem: ItemAcountRecherche = new ItemAcountRecherche();
  extractResult: ExtractResult = new ExtractResult();
  itemsPerPage: number = 20;
  listSizeItemsPerPage: Array<number> = [10, 20, 30, 40, 50];
  currentPage: number = 0;
  askedPage: number = 0;
  totalPages: any[];
  totalEntries: number = 0;
  idTiers: number;
  columns: Array<CdkTableColumns> = [
    {
      title: 'Libellé',
      responsiveTitle: 'Libellé:',
      name: 'LibellePiece',
      sort: '',
      typeColumn: CdkTableTypeColumn.html
    },
    {
      title: 'N° pièce',
      responsiveTitle: 'N° pièce:',
      name: 'DocumentNumber',
      sort: '',
      typeColumn: CdkTableTypeColumn.html
    },
    {
      title: 'Lettrage',
      responsiveTitle: 'Lettrage:',
      name: 'CodeLettrageEcriture',
      sort: '',
      typeColumn: CdkTableTypeColumn.html
    },
    {
      title: 'Émis',
      responsiveTitle: 'Émis:',
      name: 'DateEmission',
      sort: '',
      typeColumn: CdkTableTypeColumn.date
    },
    {
      title: 'Échéance',
      responsiveTitle: 'Échéance:',
      name: 'DateEcheance',
      sort: 'desc',
      typeColumn: CdkTableTypeColumn.date
    },
    {
      title: 'Montant',
      responsiveTitle: 'Montant:',
      name: 'Montant',
      sort: '',
      typeColumn: CdkTableTypeColumn.money
    },
    {
      title: 'Solde',
      responsiveTitle: 'Solde:',
      name: 'Solde',
      sort: '',
      typeColumn: CdkTableTypeColumn.money
    }
  ];

  displayedColumns: string[] = this.columns.map(x => x.name);
  hasMultiSuppliers;
  rowsInitialized = false;

  constructor(private httpServices: HttpServices,
    private fournisseurService: FournisseurService,
    private loaderService: LoaderService,
    private referenceService: ReferenceService,
    private userService: UserService,
    private trackingService: TrackingService) {

    this.idTiers = userService.IdTiers;
  }

  ngOnInit() {
    this.suppliersList = this.fournisseurService.getSuppliersInfosList();
    this.activeSupplierId = this.fournisseurService.getActiveSupplierId();
    this.hasMultiSuppliers = this.fournisseurService.getSuppliersNb() > 1;
    const colTrie = this.columns.find(col => col.sort === 'asc' || col.sort === 'desc');
    if (colTrie !== undefined) {
      this.calcRechercheTrie(colTrie);
    }
    this.find();
    this.trackingService.pushEvent('financial-account_statement');
    this.trackingService.pushPageViewGA4('financial_account_statement');
  }

  fournisseurChange(supplierId: number) {
    this.activeSupplierId = supplierId;
    this.fournisseurService.setActiveSupplierId(supplierId);
    this.askedPage = 0;
    this.find();
  }

  find(): void {
    this.loaderService.increaseLoaderCount();
    const param = this.getObligatoryParam();
    this.getOptionalParams(param);

    this.httpServices.get<ExtractResult>(this.referenceService.staticReferences.Action[config.ApiKeys.GET_EXTRAIT_LIST],
      () => { }, param).subscribe(
        response => {
          this.initList(response);
        }
      );

    this.getUrlCSV();
  }

  private getObligatoryParam(): QueryParameter[] {
    return [
      { 'Key': 'fournisseurId', 'Value': String(this.activeSupplierId) },
      { 'Key': 'pageNum', 'Value': String(this.askedPage) },
      { 'Key': 'pageSize', 'Value': String(this.itemsPerPage) }
    ];
  }

  private getOptionalParams(params: any[]) {
    if (this.rechercheItem.trieItemAccount.id !== '') {
      params.push({
        'Key': 'orderBy',
        'Value': this.rechercheItem.trieItemAccount.id + (this.rechercheItem.trieItemAsc ? ' ASC' : ' DESC')
      });
    }

    if (this.rechercheItem.referenceItem) {
      params.push({ 'Key': 'numPiece', 'Value': this.rechercheItem.referenceItem });
    }

    if (this.rechercheItem.DateDebut) {
      params.push({ 'Key': 'dateMin', 'Value': new Date(this.rechercheItem.DateDebut).toDateString() });
    }

    if (this.rechercheItem.DateFin) {
      params.push({ 'Key': 'dateMax', 'Value': new Date(this.rechercheItem.DateFin).toDateString() });
    }
  }

  calcRechercheTrie(col: CdkTableColumns) {
    this.rechercheItem.trieItemAccount.id = col.name;
    this.rechercheItem.trieItemAsc = col.sort === 'asc';
  }

  onColumnClick($event: CdkTableColumns) {
    this.calcRechercheTrie($event);
    this.find();
  }

  private initList(response: ExtractResult) {
    this.rowsInitialized = true;
    this.extractResult = response;
    this.currentPage = response.CurrentPage;
    this.totalPages = new Array(response.TotalPages + 1);
    this.totalEntries = response.TotalEntries;
    this.loaderService.decreaseLoaderCount();
  }

  getUrlCSV() {
    const params = [];
    params.push({ 'Key': 'fournisseurId', 'Value': String(this.activeSupplierId) });

    const DateDebut = moment(this.rechercheItem.DateDebut, utils.CheckType.getDateFormat(this.rechercheItem.DateDebut)).toDate();
    const DateFin = moment(this.rechercheItem.DateFin, utils.CheckType.getDateFormat(this.rechercheItem.DateFin)).toDate();

    if (!isNaN(DateDebut.getTime())) {
      params.push({ 'Key': 'dateMin', 'Value': DateDebut.toDateString() });
    }

    if (!isNaN(DateFin.getTime())) {
      params.push({ 'Key': 'dateMax', 'Value': DateFin.toDateString() });
    }

    this.urlCSV = [this.referenceService.staticReferences.ApiServer,
                   this.referenceService.staticReferences.ApiVersion,
                   this.referenceService.staticReferences.Action[config.ApiKeys.GET_EXTRAIT_LIST_CSV],
                   this.addParamsCSV(params)].join('');
  }

  private addParamsCSV(params?: QueryParameter[]): string {
    let paramQuery = '?';
    if (params) {
      for (const param of params) {
        paramQuery += '&' + param.Key + '=' + param.Value;
      }
    }
    return paramQuery;
  }

  changePage(page: number) {
    this.askedPage = page;
  }

  onChangeSizePage(sizePage: number): void {
    this.itemsPerPage = sizePage;
    this.askedPage = 0;
    this.find();
  }

  onClickTelechargerExtraitComptable(event: Event) {
    if (event.defaultPrevented) {
      return;
    }
    window.open(this.urlCSV);
  }

  filterPieces(filterCriteria: FilterCriteria) {
    this.rechercheItem.referenceItem = filterCriteria.numPiece;
    this.rechercheItem.DateDebut = filterCriteria.dateDebut;
    this.rechercheItem.DateFin = filterCriteria.dateFin;
    this.askedPage = 0;
    this.find();
  }

  ngOnDestroy() {
  }
}
